// No `price` on the product card (WI#4536, Gerd 2026-08-25).
//
// Five hardcoded strings stood here, all reading `\u20ac2.49 / user / month` -- the same
// amount for every extension. Measured against the live /api/pricing: P4 and P5 cost 2.99,
// and P2/P3 carry no per-user tier at all, only project and organisation. Four of the five
// cards advertised a wrong price, two of them a licence model that does not exist there.
//
// It was the seventh price source next to Stripe -- exactly the class #4533 removed from the
// other six. A price on a card cannot be kept correct by anything; the pricing section reads
// /api/pricing, so that is where amounts belong.
import { GENERATED_EXTENSIONS } from '../generated/extension-registry.generated';
/** Users per ADO organisation that the free tier covers. */
export const FREE_TIER_USERS = 5;
/**
 * Length of the free trial of the 'trial' products. Must equal `trialDurationDays` of their
 * billing configs -- guarded by offer-model.test.ts, because a second number that silently
 * drifts is exactly how the site came to advertise 14 days while the configs said 28.
 */
export const TRIAL_DAYS = 28;
export const PRODUCT_CONTENT = [
    {
        id: 'p1-ado-filter',
        name: 'cool\u00b7tools ADO Advanced Filter',
        abbr: 'FILT',
        offer: 'free-tier',
        tagline: 'Find any work item in seconds',
        description: 'Your backlog has thousands of items, but ADO\u2019s built-in filters only scratch the surface. ADO Filter lets you combine any field, operator and value into powerful queries \u2014 with AND/OR logic, save them as team presets, and switch between Backlog and Board view with dynamic column ordering that matches your Kanban board.',
        features: [
            'AND/OR filter logic across any ADO field',
            'Shared team filter presets',
            'Dynamic Board view with Kanban column order',
            'Drag-and-drop column configuration',
            'Available in 14 languages',
        ],
    },
    {
        id: 'p2-work-item-freeze',
        name: 'cool\u00b7tools Work Item Freeze',
        abbr: 'FRZE',
        offer: 'trial',
        tagline: 'No accidental edits after approval',
        description: 'Someone just edited a closed Sprint item and broke your audit trail. Work Item Freeze prevents this by locking work items based on iteration, area path and work-item type. Users see a clear freeze banner and can request a time-limited exception \u2014 with full audit trail. The only extension of its kind on the VS Marketplace.',
        features: [
            'Freeze rules by iteration, area path and type',
            'Exception request workflow with admin approval',
            'Visual freeze banner in the work item form',
            'Bypass groups with ADO Identity Picker',
            '\u2605 No competitor on the VS Marketplace',
        ],
    },
    {
        id: 'p3-status-freeze',
        name: 'cool\u00b7tools Status Freeze',
        abbr: 'SFRZ',
        offer: 'trial',
        tagline: 'Enforce your team\u2019s workflow',
        description: 'A Story moved from Active straight to Done, skipping Testing. Status Freeze blocks invalid state transitions in real-time. Define which transitions are allowed per work-item type, set PI-end presets that auto-lock changes before a release, and let authorized groups bypass when needed. The only extension of its kind on the VS Marketplace.',
        features: [
            'Configurable state-transition rules',
            'SAFe PI-end preset (one-click sprint lockdown)',
            'Per work-item type enforcement',
            'Real-time block with clear user message',
            '\u2605 No competitor on the VS Marketplace',
        ],
    },
    {
        id: 'p4-iteration-checker',
        name: 'cool\u00b7tools Iteration Checker',
        abbr: 'ITER',
        offer: 'free-tier',
        tagline: 'Consistent iterations, zero manual checks',
        description: 'Your Feature is in Sprint 3 but its child Stories are scattered across Sprint 1\u20134. Iteration Checker scans your entire hierarchy, flags every inconsistency by severity, and auto-fixes them with a single click. Schedule daily scans to catch problems before standup.',
        features: [
            'Recursive hierarchy scan across all levels',
            'One-click auto-fix for iteration mismatches',
            'Batch correction for bulk cleanup',
            'Scheduled daily scans via Azure Function',
            'CSV export for audit documentation',
        ],
    },
    {
        id: 'p5-multi-level-query',
        name: 'cool\u00b7tools Multi-Level Query',
        abbr: 'MQLV',
        offer: 'free-tier',
        tagline: 'See your full SAFe hierarchy at once',
        description: 'ADO queries are flat \u2014 you can\u2019t filter Epics, Features and Stories simultaneously with different criteria at each level. Multi-Level Query lets you build hierarchical queries with independent filters per depth, save them for reuse, and export the complete tree to Excel. Built for SAFe portfolio managers who need cross-level visibility.',
        features: [
            'Up to 5 hierarchy levels with independent filters',
            'Save and load named query configurations',
            'Excel export with full parent-child structure',
            'Dynamic work-item type and state filters per level',
            'Dashboard widget for quick portfolio overview',
        ],
    },
];
/**
 * Die beiden abgeleiteten Felder -- als REGEL, nicht als fuenf abgeschriebene Werte
 * (WI#4521 AC-1, Feldklasse b: ableitbar mit Transformation).
 */
const marketplaceUrlFor = (marketplaceSlug) => `https://marketplace.visualstudio.com/items?itemName=${marketplaceSlug}`;
const screenshotPathFor = (id) => `/assets/screenshots/${id}.png`;
/**
 * Die MENGE kommt aus der Ableitung, der INHALT von oben (WI#4521 AC-2).
 *
 * WARUM GEFILTERT UND NICHT MIT PLATZHALTER ERGAENZT: Eine Extension ohne Marketingtext
 * bekommt keine leere Karte auf der Startseite -- das waere ein sichtbarer Schaden statt
 * eines Hinweises. Stattdessen faellt sie hier heraus UND ein Test wird rot
 * (products.registry.test.ts). Der Fehler wird zur Build-Zeit laut, nicht beim Kunden.
 *
 * WARUM KEIN ABBRUCH ZUR LAUFZEIT: Dieses Modul laeuft im Browser. Ein throw beim
 * Modul-Laden naehme dem Besucher die ganze Seite -- ungleich teurer als eine fehlende
 * Karte. Die Skript-Konsumenten der Ableitung brechen benannt ab, weil dort niemand
 * zusieht; hier sieht jemand zu.
 */
export const PRODUCTS = GENERATED_EXTENSIONS.flatMap((ext) => {
    const content = PRODUCT_CONTENT.find((c) => c.id === ext.id);
    if (!content)
        return [];
    return [
        {
            id: content.id,
            name: content.name,
            abbr: content.abbr,
            tagline: content.tagline,
            description: content.description,
            features: content.features,
            screenshotPlaceholder: screenshotPathFor(ext.id),
            marketplaceUrl: marketplaceUrlFor(ext.marketplaceSlug),
            offer: content.offer,
        },
    ];
});
/** The product name without the brand prefix, e.g. 'Work Item Freeze'. */
export const productShortName = (product) => product.name.replace(/^cool·tools\s+/, '');
/** The real purchase path (customer portal checkout), used by both cards. */
export const BUY_LICENCE_HREF = '/customer?buy=1';
export const OFFER_CARDS = [
    {
        offer: 'free-tier',
        title: 'Team extensions',
        headline: `Free for up to ${FREE_TIER_USERS} users`,
        fromLabel: `From the ${FREE_TIER_USERS + 1}th user: from`,
        fromTier: 'perUserSingle',
        fromUnit: 'user',
        features: [
            `Free for up to ${FREE_TIER_USERS} users per ADO organisation — like Azure DevOps itself`,
            'Per-user, team (20/50/100), project and organisation licences',
            'All features included',
            '14 languages',
            'Monthly or annual billing',
        ],
        primaryCta: { label: 'Install free', href: '#products' },
        secondaryCta: { label: 'Buy a licence', href: BUY_LICENCE_HREF },
    },
    {
        offer: 'trial',
        title: 'Governance extensions',
        headline: `${TRIAL_DAYS}-day free trial`,
        fromLabel: 'Then from',
        fromTier: 'project',
        fromUnit: 'project',
        features: [
            'Built for project and organisation administrators',
            'Project or organisation licence — no per-user licence',
            'All features included',
            '14 languages',
            'Monthly or annual billing',
        ],
        primaryCta: { label: `Start ${TRIAL_DAYS}-day trial`, href: '#products' },
        secondaryCta: { label: 'Buy a licence', href: BUY_LICENCE_HREF },
    },
];
/**
 * The offer statements of a product detail page (ExtensionPage.tsx), derived from its offer.
 */
export function productOfferTexts(offer) {
    return offer === 'free-tier'
        ? {
            installCta: 'Install free — VS Marketplace',
            badge: `Free up to ${FREE_TIER_USERS} users`,
            ctaSentence: `Free for up to ${FREE_TIER_USERS} users in your organisation. From the ${FREE_TIER_USERS + 1}th user, ` +
                'choose a per-user, team, project or organisation licence. No credit card required.',
        }
        : {
            installCta: `Start ${TRIAL_DAYS}-day trial — VS Marketplace`,
            badge: `${TRIAL_DAYS}-day free trial`,
            ctaSentence: `Start with a ${TRIAL_DAYS}-day free trial, then a project or organisation licence. ` +
                'No credit card required for the trial.',
        };
}
// WI#4536: the "Bundle" tier was removed (Gerd Marinelli, 2026-08-25).
// It advertised all five extensions at \u20ac13.15 per user and month \u2014 a figure that
// summed per-user-bundle prices of P2 and P3, which are admin-focused products and
// offer project and org licences only. It was also never purchasable: `perUserBundle`
// appeared in the customer portal solely as a type declaration, with no tier label and
// no checkout path. Bundles return later in combinations that make sense in licensing
// terms; until then this page does not promise one.
// WI#4566 B1b: the 'MOST POPULAR' badge that `highlighted` drew went with the Starter/Pro
// cards -- it claimed a popularity nobody had measured (no paying customer yet).
/** 'A', 'A and B', 'A, B and C' -- for sentences that name the products of one offer. */
function joinNames(names) {
    return names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}
const namesWithOffer = (offer) => joinNames(PRODUCTS.filter((p) => p.offer === offer).map(productShortName));
/**
 * The page-wide offer statements (hero, trust bar, FAQ) -- derived from PRODUCTS[].offer and
 * the two constants above, so no sentence can promise one model for all products again
 * (WI#4566 B1b, texts approved by Gerd 2026-09-30).
 */
export const OFFER_TEXTS = {
    hero: `Free for up to ${FREE_TIER_USERS} users — or a ${TRIAL_DAYS}-day trial for the governance ` +
        'extensions. No credit card required.',
    heroCta: 'Get started free',
    trustBar: `Free up to ${FREE_TIER_USERS} users or ${TRIAL_DAYS}-day trial`,
    faqQuestion: 'Is there a free option?',
    faqAnswer: `${namesWithOffer('free-tier')} are free for up to ${FREE_TIER_USERS} users in your ADO organisation ` +
        `— like Azure DevOps itself. From the ${FREE_TIER_USERS + 1}th user you need a licence. ` +
        `${namesWithOffer('trial')} are used mainly by administrators, so they have no free tier: they ` +
        `start with a ${TRIAL_DAYS}-day free trial, then a project or organisation licence. ` +
        'No credit card required to install or try.',
};
